import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/icon-button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Card, StatusCard } from '@/components/ui/card';
import { EmptyState } from '@/components/shared/EmptyState';
import { ToolStateBadge } from '@/components/shared/ToolStateBadge';
import { cn } from '@/lib/utils';
import { ChevronDown, ChevronUp, RefreshCcw, TreePine } from 'lucide-react';
import type { Result } from '@/lib/http/HttpClient';
import type { AllToolsResponse } from '@/features/traceability/api/TraceabilityResponseType';
import type { ReferencePreview } from '../utils/transformToolsData';
import { useTranslation } from '@/hooks/useTranslation';
import { useReferenceAlias } from '@/hooks/useReferenceAlias';
import { useState, useEffect } from 'react';
import { formatDate } from '../utils/toolViewHelpers';

interface ToolsHierarchyProps {
  isLoading: boolean;
  apiResult: Result<AllToolsResponse> | null;
  hierarchy: ReferencePreview[];
  expandedReferences: Set<string>;
  onToggleReference: (referenceId: string) => void;
  onNavigateToTool: (toolId: string) => void;
  onRetry: () => void;
}

export function ToolsHierarchy({
  isLoading,
  apiResult,
  hierarchy,
  expandedReferences,
  onToggleReference,
  onNavigateToTool,
  onRetry,
}: ToolsHierarchyProps) {
  const { t, language } = useTranslation();
  const { resolve } = useReferenceAlias();
  const [visibleCount, setVisibleCount] = useState(10);

  // Reset visible count when hierarchy changes (filters applied)
  useEffect(() => {
    setVisibleCount(10);
  }, [hierarchy.length]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <div className="flex items-center gap-3">
              <Skeleton className="h-8 w-8 rounded" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-4 w-48" />
              </div>
              <Skeleton className="h-6 w-20 rounded-full" />
            </div>
          </Card>
        ))}
      </div>
    );
  }

  if (apiResult && !apiResult.ok) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-12">
        <StatusCard
          type="error"
          message={apiResult.message || t('toolView.list.hierarchy.error')}
        />
        <Button variant="outline" size="sm" onClick={onRetry} className="gap-2">
          <RefreshCcw className="size-4" aria-hidden />
          {t('toolView.list.hierarchy.retry')}
        </Button>
      </div>
    );
  }

  if (hierarchy.length === 0) {
    return (
      <EmptyState
        icon={TreePine}
        title={t('toolView.list.hierarchy.empty.title')}
        description={t('toolView.list.hierarchy.empty.description')}
      />
    );
  }

  const displayedReferences = hierarchy.slice(0, visibleCount);
  const hasMore = visibleCount < hierarchy.length;

  const handleShowMore = () => {
    setVisibleCount((prev) => prev + 10);
  };

  return (
    <div className="space-y-4">
      {displayedReferences.length > 0 && (
        <p className="text-sm text-muted-foreground">
          {t('toolView.list.pagination.showing', {
            count: displayedReferences.length,
            total: hierarchy.length,
          })}
        </p>
      )}

      {displayedReferences.map((reference) => {
        const isExpanded = expandedReferences.has(reference.reference);
        const toolsContainerId = `tools-${reference.reference}`;

        return (
          <Card
            key={reference.reference}
            className="transition-all duration-200 hover:shadow-md hover:border-primary/40"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <IconButton
                  type="button"
                  variant="outline"
                  icon={isExpanded ? ChevronUp : ChevronDown}
                  className="mt-0.5"
                  label={t('toolView.list.hierarchy.toggleDisplay', {
                    reference: reference.reference,
                  })}
                  aria-expanded={isExpanded}
                  aria-controls={toolsContainerId}
                  onClick={() => onToggleReference(reference.reference)}
                />
                <div className="space-y-1">
                  {(() => {
                    const resolved = resolve(reference.reference);
                    return (
                      <>
                        <h3 className="text-base font-semibold text-foreground">{resolved.label}</h3>
                        {resolved.hasAlias && (
                          <p className="font-mono text-xs text-muted-foreground">{resolved.canonical}</p>
                        )}
                      </>
                    );
                  })()}
                  {reference.family && (
                    <p className="eyebrow">{t(reference.family)}</p>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <Badge>
                  {t('toolView.list.hierarchy.toolCount', { count: reference.tools.length })}
                </Badge>
              </div>
            </div>

            <div
              id={toolsContainerId}
              aria-hidden={!isExpanded}
              className={cn('mt-3 grid gap-2 sm:grid-cols-2', !isExpanded && 'hidden')}
            >
              {reference.tools.map((tool) => (
                <button
                  key={tool.id}
                  type="button"
                  className="flex items-center justify-between gap-2 rounded-xl border bg-background/60 px-3 py-2.5 text-left backdrop-blur-sm transition hover:border-primary/40 hover:bg-primary/5 hover:shadow-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                  onClick={() => onNavigateToTool(tool.id)}
                  aria-label={t('toolView.list.hierarchy.openTool', { toolId: tool.id })}
                  title={t('toolView.list.hierarchy.openTool', { toolId: tool.id })}
                >
                  <span className="flex flex-col items-start gap-1 text-left">
                    <span className="text-sm font-semibold text-foreground">{tool.id}</span>
                    <span className="text-xs text-muted-foreground">
                      {tool.traceCount > 0
                        ? `${tool.traceCount} ${t(tool.traceCount === 1 ? 'toolView.list.traceCount.one' : 'toolView.list.traceCount.many')} · ${formatDate(tool.lastTraceDate, language, t, 'toolView.list.hierarchy.noTrace')}`
                        : t('toolView.list.hierarchy.noTrace')}
                    </span>
                  </span>
                  <ToolStateBadge toolState={tool.toolState} />
                </button>
              ))}
            </div>
          </Card>
        );
      })}

      {hasMore && (
        <div className="flex justify-center pt-2">
          <Button variant="outline" onClick={handleShowMore}>
            {t('toolView.list.pagination.showMore')}
          </Button>
        </div>
      )}
    </div>
  );
}
